import {Breadcrumb, ProgressIndicator} from '@/components/atoms';
import {PageHeader, SaveCancelFooter} from '@/components/molecules';
import {PageContainer} from '@/components/organisms';
import {CreatePointMultiplierSection} from '@/pages/PointMultiplier/CreatePointMultiplierSection';
import {useAppDispatch} from '@/store';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  isProductsUploadComplete,
  useCreatePointMultiplier
} from '@/pages/PointMultiplier/CreatePointMultiplier-hook';
import {setPointMultiplierFormData} from '@/store/slices/pointMultiplier';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {usePointMultiplier} from '@/hooks/usePointMultiplier';
import {getModal} from '@/utils/CommonModal';
import {logger} from '@/helpers/logger';
export const CreatePointMultiplier = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    formData,
    setFormData,
    formError,
    setFormError,
    rulesCriteriaJsonSchema,
    segmentJsonSchema,
    categoryJsonSchema,
    channelJsonSchema,
    rulesCriteriaUISchema,
    handleFormChange,
    validateFormData,
    disableNext,
    setDisableNext,
    formFields,
    handleUpdateValue,
    createReqObj,
    progressBarSteps,
    isCancel,
    isNameValid,
    setIsNameValid,
    handleCancel,
    onClickPrimaryButton,
    onClickSecondaryButton,
    handleValidationModalClose,
    handleStepClick
    // countyName,
  } = useCreatePointMultiplier();

  const {
    handleDraft: handlePointMultiplierDraft,
    handleNameValidation: handleNameValidation
  } = usePointMultiplier();

  const handleDraft = async () => {
    try {
      const isValid = await validateName();
      if (isValid) {
        const reqObj = createReqObj('draft');
        const draftResponse = await handlePointMultiplierDraft(reqObj);
        if (!draftResponse?.success) {
          return;
        }
      }
    } catch (error) {
      logger.error('Error saving draft:', error);
      return;
    }
  };

  const handleNext = async () => {
    try {
      const isValidName = await validateName();

      if (!isValidName) return;

      const isValidForm = validateFormData();
      dispatch(setPointMultiplierFormData(formData));

      if (isValidForm) {
        navigate(
          formData?.rulesCriteria !== 'Products'
            ? ROUTES.POINT_MULTIPLIER_REVIEW
            : ROUTES.POINT_MULTIPLIER_PRODUCTS
        );
      }
    } catch (error) {
      logger.error('Error in handleNext:', error);
    }
  };

  const validateName = async () => {
    const modifierName = formData?.modifierName;

    // Check if name is empty or whitespace only
    if (!modifierName || String(modifierName).trim() === '') {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Multiplier Name is mandatory',
          type: 'error'
        })
      );
      return false;
    }

    const id = formData?.id || null;
    const uuid = formData?.uuid || null;
    const params = {
      id,
      uuid,
      name: modifierName.trim()
    };
    const resp = await handleNameValidation(params);
    const isValid = resp?.success === true;
    setIsNameValid(isValid);
    return isValid;
  };

  const handleSetupStepClick = async (step) => {
    if (step <= 1) return;
    try {
      const isValidName = await validateName();
      if (!isValidName) return;

      const isValidForm = validateFormData();
      dispatch(setPointMultiplierFormData(formData));
      if (!isValidForm) return;

      const hasProductsStep =
        formData?.rulesCriteria?.trim()?.toLowerCase() === 'products';
      if (hasProductsStep && step > 2 && !isProductsUploadComplete(formData)) {
        handleStepClick(2);
        return;
      }
      handleStepClick(step);
    } catch (error) {
      logger.error('Error in handleSetupStepClick:', error);
    }
  };

  const getCustomModal = (type) => {
    switch (type) {
      case 'CANCEL_MODAL':
        return getModal({
          title: 'Are you sure you want to cancel?',
          text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
          showPrimaryButton: true,
          primaryButtonText: 'Yes',
          onClickPrimaryButton: onClickPrimaryButton,
          showSecondaryButton: true,
          secondaryButtonText: 'No',
          onClickSecondaryButton: onClickSecondaryButton,
          showModal: isCancel,
          onCloseModal: handleCancel,
          hideCloseButton: false
        });
      case 'NAME_VALIDATION_MODAL':
        return getModal({
          title: 'Duplicate Name Detected',
          text: (
            <>
              It looks like there's already a Point Multiplier with this name.
              <br />
              To avoid confusion, please enter a unique name before continuing.
            </>
          ),
          showPrimaryButton: true,
          primaryButtonText: 'Edit Name',
          onClickPrimaryButton: handleValidationModalClose,
          showSecondaryButton: false,
          showModal: !isNameValid,
          onCloseModal: handleValidationModalClose,
          hideCloseButton: true
        });
      default:
        return null;
    }
  };

  return (
    <div className='salesTargetsContainer flex flex-col'>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              label: '',
              href: ROUTES.HOME
            },
            {
              label: 'Point Multiplier',
              href: ROUTES.POINT_MULTIPLIER
            },

            {
              label: 'Create Point Multiplier',
              href: ROUTES.POINT_MULTIPLIER
            }
          ]
        }}
        id={'pm_bradcrumb'}
      />
      <PageHeader title='Create Point Multiplier' />

      <div className='grid p-8 '>
        <div className='w-3/5 justify-self-center'>
          <ProgressIndicator
            currentStep={1}
            completed={false}
            isDark={false}
            // orientation="horizontal"
            steps={progressBarSteps?.length || 0}
            titles={progressBarSteps || []}
            onStepClick={handleSetupStepClick}
            allowAnyStepClick
          />
        </div>
      </div>
      <PageContainer>
        {isCancel && getCustomModal('CANCEL_MODAL')}
        {!isNameValid && getCustomModal('NAME_VALIDATION_MODAL')}
        <CreatePointMultiplierSection
          formData={formData}
          setFormData={setFormData}
          rulesCriteriaJsonSchema={rulesCriteriaJsonSchema}
          rulesCriteriaUISchema={rulesCriteriaUISchema}
          formError={formError}
          setFormError={setFormError}
          formFields={formFields}
          disableNext={disableNext}
          setDisableNext={setDisableNext}
          handleUpdateValue={handleUpdateValue}
          segmentJsonSchema={segmentJsonSchema}
          categoryJsonSchema={categoryJsonSchema}
          channelJsonSchema={channelJsonSchema}
          handleFormChange={handleFormChange}
        />
      </PageContainer>

      <SaveCancelFooter
        handleCancel={handleCancel}
        nextAction={handleNext}
        draftAction={handleDraft}
        FormError={disableNext}
        hideSaveDraft={false}
      />
    </div>
  );
};
